import React, { useEffect } from 'react';
import { getActiveJobLogsByJobId, getJobLogsByJobId, postJobManualRun, putJob } from '@/redux/actions/jobsActions';
import { useSelector } from 'react-redux';
import selectors from '@/redux/selectors/selectors';
import useInterval from './lib/useInterval';
import { useNavigate } from 'react-router-dom';
import { FiPause, FiPlay, MdMemory, MdSchedule } from '@/components/react-icons/main';
import { jobMemoryByJobId } from '@/redux/selectors/jobsSelectors';
import { formatJobInterval, jobIntervalMap } from './lib/jobInterval';
import { camelToTitleCase } from '@shared/utils/camelToTitleCase.js';
import copyToClipboard from '@/lib/copyToClipboard';
import sendToast from '@/lib/sendToast.js';
import Button from '@/components/Button.jsx';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';

const JobScreenActions = () => {
  const navigate = useNavigate();
  const canEditJob = useArgSelector(selectors.permission, 'job:edit');
  const canRunJob = useArgSelector(selectors.permission, 'job:run');
  const subpageId = useArgSelector(selectors.param, 'subpageId');
  const jobId = useArgSelector(selectors.param, 'jobId');
  const section = useArgSelector(selectors.param, 'section');
  const jobIsRunning = useArgSelector(selectors.jobIsRunning, jobId);

  const admin = useSelector(selectors.admin);
  const job = useArgSelector(selectors.jobById, jobId);

  const jobMemory = useArgSelector(jobMemoryByJobId, jobId) || {};
  const jobMemoryEntries = Object.entries(jobMemory).map(([key, value]) => ({
    id: key,
    key: camelToTitleCase(key),
    value
  }));

  const { overrideRef } = useInterval(() => getActiveJobLogsByJobId({ jobId }), [jobId], {
    interval: 10000,
    overrideInterval: 1000
  });

  const jobLogs = useArgSelector(selectors.jobLogsByJobId, jobId);

  const midLogs = (jobLogs?.logs || []).filter((n) => !['1', '2'].includes(String(n?.statusId)));
  overrideRef.current = !!midLogs?.length;

  const runJob = (e) => {
    const force = e.ctrlKey || e.metaKey;
    postJobManualRun({ id: jobId, force }, (manualRunResponse) => {
      getActiveJobLogsByJobId({ jobId }, () => {
        if (subpageId && manualRunResponse?.jobLogId) {
          navigate(`/jobs/${jobId}/logs/${manualRunResponse?.jobLogId}`);
        }
      });
    });
  };

  const handleWindowKeydown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      runJob(e);
    }
  };

  const handleRunClick = (e) => {
    runJob(e);
  };

  const handleRunningToggle = () => {
    if (!canEditJob) {
      sendToast({ message: 'You do not have permission to perform this action.' });
      return;
    }
    const newStatus = job?.status === 'RUNNING' ? 'PAUSED' : 'RUNNING';
    putJob({ id: jobId, status: newStatus });
  };

  const handleIntervalClick = (option) => {
    if (!canEditJob) {
      sendToast({ message: 'You do not have permission to perform this action.' });
      return;
    }

    if (option?.value && admin) {
      putJob({ id: jobId, interval: option?.value });
    } else if (option?.label === 'No Interval' && admin) {
      putJob({ id: jobId, interval: null, status: 'PAUSED' });
    }
  };

  const handleCopyClick = () => {
    copyToClipboard(job?.code);
    sendToast({ message: 'Job code copied to clipboard!' });
  };

  useEffect(() => {
    window.addEventListener('keydown', handleWindowKeydown);
    return () => window.removeEventListener('keydown', handleWindowKeydown);
  }, [section]);

  useEffect(() => {
    getJobLogsByJobId({ jobId, page: 1 });
  }, [jobId]);

  const intervalOptions = Object.entries(jobIntervalMap).map(([key, value]) => ({ value, label: key }));
  const intervalLabel = jobIntervalMap[job?.interval] || formatJobInterval(job?.interval);

  return (
    <>
      <Button size="sm" onClick={handleCopyClick}>
        Copy Job Code
      </Button>
      {jobMemoryEntries?.length > 0 && (
        <Button size="sm" icon={MdMemory} className={'job-memory-button'}>
          Job Memory
        </Button>
      )}
      <Button size="sm" icon={job?.status === 'RUNNING' ? MdSchedule : FiPause} onClick={handleRunningToggle} onOptionClick={handleIntervalClick} options={job?.status === 'RUNNING' && admin ? intervalOptions : null}>
        {job?.status === 'RUNNING' ? `Runs ${intervalLabel}` : 'Paused'}
      </Button>
      {canRunJob && (
        <Button className={`primary run-button ${jobIsRunning ? 'error' : ''}`} size="sm" icon={FiPlay} onClick={handleRunClick} loading={jobIsRunning}>
          <div>{jobIsRunning ? 'Running' : 'Run'}</div>
        </Button>
      )}
    </>
  );
};

export default JobScreenActions;
